import React, { FC, ReactElement, Suspense, useEffect, useState } from 'react'
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom'
import { CssBaseline, Theme } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import TagManager from 'react-gtm-module'
import { getQueryParamsData } from '@dg-util'

import './i18n'
import brandTheme from 'BrandTheme'
import history from './routes/history'
import Loading from './pages/qb/Loading/Page'
import { FSMService, FSMProvider } from './FSM'
import { setInspectHandler } from './FSM/inspector'
import packageJson from '../package.json'
import errorLoggerService from './service/errorLogger.service'
import { PartnerName } from './types/interface/quote.interface'
import CONFIG, { ENV } from './config'
import { IconType } from './types/interface/icon.interface'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import queryClient from './queryClient'
import ContextProvider from '@dg-shared/ContextProvider'
import AgentStatus from '@dg-shared/AgentStatus'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { CacheProvider } from '@emotion/react'
import { TssCacheProvider } from 'tss-react'
import { muiCache, tssCache } from './styles/emotionCaches'

errorLoggerService.setPartner(PARTNER_NAME as PartnerName)

// xState visualizer (inspector) switch
const xStateVisualizerEnabled = localStorage.getItem('xStateVisualizerEnabled')

// Enable XState inspector in debug mode with enabled DEV_TOOLS and xStateVisualizerEnabled true.
// Dynamic import: `@statelyai/inspect` ships ESM only, and this keeps a dev tool out of the
// production bundle and out of jest's module graph.
if (CONFIG.DEV_TOOLS && xStateVisualizerEnabled) {
  import('@statelyai/inspect')
    .then(({ createBrowserInspector }) => {
      setInspectHandler(createBrowserInspector().inspect)
    })
    .catch((): void => undefined)
}

// Get product name, GTM data layer, sellingChannel from initialization from URL query param under the hood)
const queryParamsData = getQueryParamsData()
const productName = queryParamsData.productName
const dataLayerSettings = queryParamsData.dataLayerSettings

// Initialize GTM with TRACKING_ID targeted on specific BRAND, PRODUCT, ENV
// Set additional data layer settings for marketing and analytics
TagManager.initialize({
  gtmId: CONFIG.TRACKING_ID[productName][ENV],
  dataLayer: dataLayerSettings,
})

const AppWrapper: FC<{
  children?: ReactElement[] | ReactElement
  queryClientTest?: QueryClient
  testInitialContextState?: Record<string, unknown>
}> = ({ children, queryClientTest, testInitialContextState }) => {
  const [theme, setTheme] = useState<Theme>(brandTheme.theme)

  useEffect(() => {
    brandTheme.setCreateThemeCallback(setTheme)
  }, [])

  const content = children ? (
    // `HistoryRouter` (not `BrowserRouter`) because the FSM drives navigation through the
    // module-level `routes/history` object from outside React — see FSM/util.setClientHistory.
    // `BrowserRouter` would own a second, independent history for the same window.
    <HistoryRouter history={history}>
      <FSMProvider>{children}</FSMProvider>
    </HistoryRouter>
  ) : (
    <Loading />
  )

  return (
    <Suspense fallback={IconType.LOADING}>
      <QueryClientProvider client={queryClientTest || queryClient}>
        <ContextProvider testInitialContextState={testInitialContextState}>
          <CacheProvider value={muiCache}>
            <TssCacheProvider value={tssCache}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <LocalizationProvider dateAdapter={AdapterDateFns}>{content}</LocalizationProvider>
                <p hidden>{`app v${packageJson.version}`}</p>
                <AgentStatus flowService={FSMService.flowService} />
              </ThemeProvider>
            </TssCacheProvider>
          </CacheProvider>
        </ContextProvider>
      </QueryClientProvider>
    </Suspense>
  )
}

export default AppWrapper
